/**
 * components/brief-builder/BriefRecommendStep.tsx
 *
 * Step 3 — Human review and editing of AI-recommended dashboards and widgets.
 * Roles: Business Analyst
 *
 * Rules enforced in UI:
 *   - Max 6 widgets per dashboard
 *   - Widgets must be from the library (dropdown)
 *   - Subcategories only have filter edits, no widget changes
 */
import { useEffect, useState } from 'react'
import type {
  BriefSession,
  DashboardDraft,
  ModuleType,
  SubcategoryDraft,
  SubcategoryType,
  WidgetDefinition,
  WidgetInstanceConfig,
  UpdateStructurePayload,
} from '@/types/brief-builder'
import { SUBCATEGORY_TYPE_LABELS } from '@/types/brief-builder'

const MAX_WIDGETS = 6

interface Props {
  session: BriefSession
  widgets: WidgetDefinition[]
  onSave: (payload: UpdateStructurePayload) => void
  onValidate: () => void
  loading?: boolean
}

export default function BriefRecommendStep({ session, widgets, onSave, onValidate, loading }: Props) {
  const rec = session.recommendation
  const [modules,    setModules]    = useState<ModuleType[]>(rec?.modules ?? ['dashboards'])
  const [dashboards, setDashboards] = useState<DashboardDraft[]>(rec?.dashboards ?? [])

  // Per-slot widget configs: dashId → widgetId → WidgetInstanceConfig
  const [widgetConfigs, setWidgetConfigs] = useState<
    Record<string, Record<string, WidgetInstanceConfig>>
  >(() => {
    const init: Record<string, Record<string, WidgetInstanceConfig>> = {}
    for (const d of rec?.dashboards ?? []) {
      init[d.id] = { ...(d.widget_configs ?? {}) }
    }
    return init
  })
  // Which cell is expanded for config: "dashId:widgetId"
  const [expandedCell, setExpandedCell] = useState<string | null>(null)

  const toggleCell = (dashId: string, widgetId: string) => {
    const key = `${dashId}:${widgetId}`
    setExpandedCell(prev => (prev === key ? null : key))
  }

  const updateWidgetConfig = (
    dashId: string,
    widgetId: string,
    patch: Partial<WidgetInstanceConfig>,
  ) => {
    setWidgetConfigs(prev => ({
      ...prev,
      [dashId]: {
        ...(prev[dashId] ?? {}),
        [widgetId]: { ...(prev[dashId]?.[widgetId] ?? {}), ...patch },
      },
    }))
    // mark the dashboard as human_edited
    setDashboards(prev =>
      prev.map(d => d.id === dashId ? { ...d, human_edited: true } : d)
    )
  }

  // ── Module toggles ─────────────────────────────────────────────────────────────────

  // Build widget lookup by id
  const widgetMap = Object.fromEntries(widgets.map(w => [w.id, w]))

  const toggleModule = (m: ModuleType) =>
    setModules(prev =>
      prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]
    )

  // ── Dashboard edits ────────────────────────────────────────────────────────

  const updateDashboard = (id: string, patch: Partial<DashboardDraft>) =>
    setDashboards(prev =>
      prev.map(d => d.id === id ? { ...d, ...patch, human_edited: true } : d)
    )

  const removeDashboard = (id: string) =>
    setDashboards(prev => prev.filter(d => d.id !== id))

  // ── Widget edits ───────────────────────────────────────────────────────────

  const addWidget = (dashId: string, widgetId: string) =>
    setDashboards(prev =>
      prev.map(d => {
        if (d.id !== dashId) return d
        if (d.widget_ids.includes(widgetId)) return d
        const usedSlots = d.widget_ids.reduce(
          (sum, wid) => sum + (widgetMap[wid]?.default_slots ?? 1), 0
        )
        const newSlots = widgetMap[widgetId]?.default_slots ?? 1
        if (usedSlots + newSlots > MAX_WIDGETS) return d
        return { ...d, widget_ids: [...d.widget_ids, widgetId], human_edited: true }
      })
    )

  const removeWidget = (dashId: string, widgetId: string) =>
    setDashboards(prev =>
      prev.map(d =>
        d.id === dashId
          ? { ...d, widget_ids: d.widget_ids.filter(w => w !== widgetId), human_edited: true }
          : d
      )
    )

  // ── Subcategory edits ──────────────────────────────────────────────────────

  const addSubcategory = (dashId: string) =>
    setDashboards(prev =>
      prev.map(d =>
        d.id === dashId
          ? {
              ...d,
              subcategories: [
                ...d.subcategories,
                { id: `sub-${Date.now()}`, name: 'New Subcategory', type: 'custom' as SubcategoryType, filters: {} },
              ],
              human_edited: true,
            }
          : d
      )
    )

  const updateSubcategory = (dashId: string, scId: string, patch: Partial<SubcategoryDraft>) =>
    setDashboards(prev =>
      prev.map(d =>
        d.id === dashId
          ? {
              ...d,
              subcategories: d.subcategories.map(sc =>
                sc.id === scId ? { ...sc, ...patch } : sc
              ),
              human_edited: true,
            }
          : d
      )
    )

  const removeSubcategory = (dashId: string, scId: string) =>
    setDashboards(prev =>
      prev.map(d =>
        d.id === dashId
          ? { ...d, subcategories: d.subcategories.filter(sc => sc.id !== scId), human_edited: true }
          : d
      )
    )

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSaveAndValidate = () => {
    // Merge widgetConfigs back into dashboards before saving
    const dashesWithConfigs = dashboards.map(d => ({
      ...d,
      widget_configs: widgetConfigs[d.id] ?? {},
    }))
    onSave({
      modules,
      dashboards: dashesWithConfigs,
      clarification_answers: session.clarification_answers,
    })
  }

  // ── Eligible widgets for a dashboard family ────────────────────────────────
  const eligibleWidgets = (family: string) => widgets.filter(w => w.category === family)

  if (!rec) return (
    <div className="text-center py-16 text-slate-400">
      No recommendations yet. Submit the brief first.
    </div>
  )

  return (
    <div className="max-w-4xl mx-auto px-5 py-10 space-y-8">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Review Recommendations</h2>
        <p className="text-sm text-slate-500 mt-1">
          Edit dashboards, widgets, and subcategories. Max {MAX_WIDGETS} widgets per dashboard.
        </p>
      </div>

      {/* Module toggles */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Enabled Modules
        </h3>
        <div className="flex gap-3 flex-wrap">
          {(['dashboards', 'ai_assistant', 'surveys'] as const).map(m => (
            <button
              key={m}
              onClick={() => toggleModule(m)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors
                ${modules.includes(m)
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                }
                ${m === 'dashboards' ? 'cursor-default' : ''}
              `}
              disabled={m === 'dashboards'}
              title={m === 'dashboards' ? 'Dashboards is always enabled' : ''}
            >
              {m.replace('_', ' ')}
            </button>
          ))}
        </div>
      </section>

      {/* Dashboard cards */}
      <section className="space-y-6">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Dashboards
        </h3>

        {dashboards.map(dash => {
          // All library widgets not yet in this dashboard (cross-family after engine padding)
          const available = widgets.filter(w => !dash.widget_ids.includes(w.id))
          const rationale = rec.rationale?.[dash.id]

          return (
            <div key={dash.id}
              className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-start justify-between px-5 py-4 bg-slate-50 border-b border-slate-100">
                <div className="flex-1 space-y-1">
                  <input
                    className="text-base font-semibold text-slate-900 bg-transparent border-b
                               border-dashed border-slate-300 focus:outline-none focus:border-indigo-400 w-full"
                    value={dash.name}
                    onChange={e => updateDashboard(dash.id, { name: e.target.value })}
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 font-mono">{dash.family}</span>
                    {dash.human_edited && (
                      <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200
                                       rounded-full px-1.5 py-0.5 font-medium">edited</span>
                    )}
                  </div>
                  {rationale && (
                    <p className="text-xs text-slate-500 italic mt-1">{rationale}</p>
                  )}
                </div>
                <button
                  onClick={() => removeDashboard(dash.id)}
                  className="text-red-400 hover:text-red-600 ml-4 transition-colors"
                  title="Remove dashboard"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path d="M2 4h12M6 4V2h4v2M5 4v9h6V4" stroke="currentColor"
                          strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              {/* 2×3 Widget Grid */}
              <div className="px-5 py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-600">Widget layout — 2 × 3</p>
                  {(() => {
                    const usedSlots = dash.widget_ids.reduce((sum, wid) => {
                      const cfg = widgetConfigs[dash.id]?.[wid] ?? {}
                      return sum + (cfg.slots ?? widgetMap[wid]?.default_slots ?? 1)
                    }, 0)
                    const emptySlots = MAX_WIDGETS - usedSlots
                    return emptySlots <= 0
                      ? <span className="text-[10px] text-emerald-600 font-medium">✓ Full grid</span>
                      : <span className="text-[10px] text-amber-600 font-medium">
                          {emptySlots} empty slot{emptySlots !== 1 ? 's' : ''}
                        </span>
                  })()}
                </div>

                {/* Full-dashboard widget spans all 6 cells */}
                {dash.widget_ids.length === 1 && widgetMap[dash.widget_ids[0]]?.slot_usage === 'full_dashboard' ? (
                  <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-4
                                  flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-indigo-700">
                        {widgetMap[dash.widget_ids[0]]?.name}
                      </p>
                      <p className="text-xs text-indigo-400 mt-0.5">Full-dashboard · occupies all 6 slots</p>
                    </div>
                    <button
                      onClick={() => removeWidget(dash.id, dash.widget_ids[0])}
                      className="text-indigo-300 hover:text-red-500 transition-colors"
                    >×</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {/* Filled widget cells — each spans its default_slots columns */}
                    {dash.widget_ids.map((wid) => {
                      const wDef = widgetMap[wid]
                      if (!wDef) return null
                      const sel = rec.widget_selections.find(s => s.widget_id === wid)
                      const cellKey = `${dash.id}:${wid}`
                      const isExpanded = expandedCell === cellKey
                      const cfg = widgetConfigs[dash.id]?.[wid] ?? {}
                      const slots = cfg.slots ?? wDef.default_slots ?? 1
                      const colSpan = slots >= 3 ? 'col-span-3' : slots === 2 ? 'col-span-2' : 'col-span-1'
                      return (
                        <div
                          key={wid}
                          className={`${colSpan} rounded-lg border min-h-[72px] flex flex-col
                                      transition-colors bg-indigo-50 border-indigo-200`}
                        >
                          <>
                            {/* Widget header row */}
                            <div className="flex items-start justify-between gap-1 p-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-semibold text-indigo-700 leading-tight truncate"
                                   title={wDef.description}>
                                  {cfg.title || wDef.name}
                                </p>
                                <p className="text-[9px] text-indigo-400 leading-tight truncate mt-0.5">
                                  {wDef.name}
                                  {sel && <span className="ml-1 opacity-60">· {Math.round(sel.confidence * 100)}%</span>}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {/* Configure button */}
                                <button
                                  onClick={() => toggleCell(dash.id, wid)}
                                  title="Configure widget"
                                  className={`text-[10px] px-1 py-0.5 rounded transition-colors
                                    ${isExpanded
                                      ? 'bg-indigo-200 text-indigo-800'
                                      : 'text-indigo-300 hover:text-indigo-600 hover:bg-indigo-100'
                                    }`}
                                >⚙</button>
                                {/* Remove */}
                                <button
                                  onClick={() => removeWidget(dash.id, wid)}
                                  className="text-indigo-300 hover:text-red-500 transition-colors text-xs"
                                  title="Remove"
                                >✕</button>
                              </div>
                            </div>

                            {/* Inline config panel */}
                            {isExpanded && (
                              <div className="border-t border-indigo-100 px-2 pb-2 pt-1 space-y-1.5"
                                   onClick={e => e.stopPropagation()}>
                                {/* Title */}
                                <div>
                                  <label className="text-[9px] font-semibold text-indigo-500 uppercase tracking-wide">
                                    Title
                                  </label>
                                  <input
                                    className="mt-0.5 w-full text-[10px] border border-indigo-200 rounded px-1.5 py-0.5
                                               bg-white focus:outline-none focus:border-indigo-400"
                                    placeholder={wDef.name}
                                    value={cfg.title ?? ''}
                                    onChange={e => updateWidgetConfig(dash.id, wid, { title: e.target.value || undefined })}
                                  />
                                </div>
                                {/* Newsfeed type (only for newsfeed widgets) */}
                                {wDef.newsfeed_type_options.length > 0 && (
                                  <div>
                                    <label className="text-[9px] font-semibold text-indigo-500 uppercase tracking-wide">
                                      Newsfeed Type
                                    </label>
                                    <select
                                      className="mt-0.5 w-full text-[10px] border border-indigo-200 rounded px-1.5 py-0.5
                                                 bg-white focus:outline-none focus:border-indigo-400"
                                      value={cfg.newsfeed_type ?? ''}
                                      onChange={e => updateWidgetConfig(dash.id, wid, { newsfeed_type: e.target.value || undefined })}
                                    >
                                      <option value="">— default —</option>
                                      {wDef.newsfeed_type_options.map((opt: string) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                                {/* Tooltip */}
                                <div>
                                  <label className="text-[9px] font-semibold text-indigo-500 uppercase tracking-wide">
                                    Tooltip
                                  </label>
                                  <input
                                    className="mt-0.5 w-full text-[10px] border border-indigo-200 rounded px-1.5 py-0.5
                                               bg-white focus:outline-none focus:border-indigo-400"
                                    placeholder="Help text shown on hover…"
                                    value={cfg.tooltip ?? ''}
                                    onChange={e => updateWidgetConfig(dash.id, wid, { tooltip: e.target.value || undefined })}
                                  />
                                </div>
                              </div>
                            )}
                          </>
                        </div>
                      )
                    })}
                    {/* Empty slot cells — one per remaining slot */}
                    {(() => {
                      const usedSlots = dash.widget_ids.reduce((sum, wid) => {
                        const cfg = widgetConfigs[dash.id]?.[wid] ?? {}
                        return sum + (cfg.slots ?? widgetMap[wid]?.default_slots ?? 1)
                      }, 0)
                      return Array.from({ length: Math.max(0, MAX_WIDGETS - usedSlots) }).map((_, i) => (
                        <div key={`empty-${i}`}
                          className="col-span-1 rounded-lg border min-h-[72px] flex flex-col
                                     bg-slate-50 border-dashed border-slate-200"
                        >
                          {available.length > 0 ? (
                            <div className="flex items-center justify-center h-full p-2">
                              <select
                                className="text-[10px] text-slate-400 bg-transparent cursor-pointer
                                           focus:outline-none w-full text-center"
                                value=""
                                onChange={e => { if (e.target.value) addWidget(dash.id, e.target.value) }}
                              >
                                <option value="">＋ add</option>
                                {available.map(w => (
                                  <option key={w.id} value={w.id}>{w.name}</option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <span className="text-[10px] text-slate-300">—</span>
                            </div>
                          )}
                        </div>
                      ))
                    })()}
                  </div>
                )}

                {/* Subcategories */}
                {(dash.subcategories.length > 0 || true) && (
                  <div className="pt-2 border-t border-slate-100">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-slate-500">
                        Subcategories
                        <span className="ml-1 font-normal text-slate-400">
                          (content filters only — widget set is shared)
                        </span>
                      </p>
                      <button
                        onClick={() => addSubcategory(dash.id)}
                        className="text-[11px] text-indigo-500 hover:text-indigo-700 font-medium"
                      >
                        + Add
                      </button>
                    </div>
                    <div className="space-y-2">
                      {dash.subcategories.map(sc => (
                        <div key={sc.id} className="flex items-start gap-2 bg-slate-50 rounded-lg p-2">
                          {/* Type badge/selector */}
                          <select
                            className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200
                                       rounded-md px-1 py-0.5 focus:outline-none focus:border-indigo-400 shrink-0"
                            value={sc.type ?? 'custom'}
                            onChange={e => updateSubcategory(dash.id, sc.id, { type: e.target.value as SubcategoryType })}
                          >
                            {(Object.entries(SUBCATEGORY_TYPE_LABELS) as [SubcategoryType, string][]).map(([val, label]) => (
                              <option key={val} value={val}>{label}</option>
                            ))}
                          </select>
                          {/* Name */}
                          <input
                            className="text-xs font-medium text-slate-700 bg-transparent border-b
                                       border-dashed border-slate-300 focus:outline-none focus:border-indigo-400 w-28"
                            value={sc.name}
                            onChange={e => updateSubcategory(dash.id, sc.id, { name: e.target.value })}
                            placeholder="Name"
                          />
                          {/* Filters */}
                          <input
                            className="text-xs text-slate-500 bg-transparent border-b border-dashed
                                       border-slate-200 focus:outline-none focus:border-indigo-400 flex-1"
                            value={Object.entries(sc.filters).map(([k,v]) => `${k}=${v}`).join(', ')}
                            onChange={e => {
                              const filters: Record<string, string> = {}
                              e.target.value.split(',').forEach(pair => {
                                const [k, v] = pair.split('=')
                                if (k && v) filters[k.trim()] = v.trim()
                              })
                              updateSubcategory(dash.id, sc.id, { filters })
                            }}
                            placeholder="key=value, key2=value2"
                          />
                          <button
                            onClick={() => removeSubcategory(dash.id, sc.id)}
                            className="text-slate-300 hover:text-red-400 text-xs shrink-0"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </section>

      <div className="flex justify-end gap-3">
        <button
          onClick={handleSaveAndValidate}
          disabled={loading}
          className="px-6 py-2.5 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold
                     hover:bg-slate-200 disabled:opacity-40 transition-colors"
        >
          Save Structure
        </button>
        <button
          onClick={() => {
            handleSaveAndValidate()
            setTimeout(onValidate, 200)
          }}
          disabled={loading}
          className="px-6 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold
                     hover:bg-indigo-700 disabled:opacity-40 transition-colors"
        >
          {loading ? 'Validating…' : 'Validate & Continue →'}
        </button>
      </div>
    </div>
  )
}
